/* eslint-disable no-bitwise */
/* eslint-disable ts/prefer-literal-enum-member */
/* eslint-disable no-restricted-syntax */
import type { LibraryLayerBehavior } from "../interop/dotnetExports/document";
import type {
  AxFontStyle,
  AxStringAlignment,
  Baseline,
  FontStretch,
  TextListInfo,
} from "../platform/attributedString";
import { getCurrentSelection, getLibraryBehaviorContextMenuItem as getLibraryBehaviorContextMenuItemProxy, getSelectionContextMenuItem, getSelectionImgThumbnail, showSelectionContextMenu } from "../interop/dotnetExports/bridge";
import { getGradientModeGeometryInfo, getShapeManagerSelectedPoints } from "../interop/dotnetExports/editor";
import { useBinaryReader } from "./binary";
import { useBusEmitterListener } from "./index";

// TODO: Relocate enums & types into a more appropriate location -> another "AxEnums"? -> AxEnums.ts

export enum SliceType {
  Vertical = 1,
  Horizontal = 2,
  BothSlices = 3,
  Crop = 8,
}

export enum AxLetterCasing {
  Normal = 0,
  Uppercase = 1,
  Lowercase = 2,
}

export const enum AxPosition {
  // Indeterminate = Number.MIN_SAFE_INTEGER,
  Indeterminate = -1,

  None = 0x0,
  Left = 0x1,
  Top = 0x2,
  Right = 0x4,
  Bottom = 0x8,

  TopLeft = Top | Left,
  TopRight = Top | Right,
  BottomLeft = Bottom | Left,
  BottomRight = Bottom | Right,

  FillHorizontal = Left | Right,
  FillVertical = Top | Bottom,

  TopFill = Top | FillHorizontal,
  BottomFill = Bottom | FillHorizontal,
  LeftFill = Left | FillVertical,
  RightFill = Right | FillVertical,
  All = 0xF,

  // these are for BorderVisibility
  LeftRight = Left | Right,
  TopBottom = Top | Bottom,
  NoBottom = Left | Top | Right,
  NoRight = Left | Top | Bottom,
  NoTop = Left | Right | Bottom,
  NoLeft = Top | Right | Bottom,

  CenterHorizontal = 0x100,
  CenterVertical = 0x200,
  Center = CenterHorizontal | CenterVertical,
}

export const enum AxLineStyle {
  Solid = 1,
  LongDash = 2,
  MediumDash = 3,
  ShortDash = 4,
  DashDot = 5,
  DashDotDot = 6,
  LongDashShortDash = 7,
  Dot = 8,
  None = 9,
}

export const enum AxLineJoin {
  Miter,
  Bevel,
  Round,
}

export const enum AxArrowStyle {
  // Indeterminate =    int.MinValue,
  Indeterminate = -1,

  None = 1,
  ReverseCrow = 2,
  FilledTriangle = 3,
  SkinnyTriangle = 4,
  Diamond = 5,
  CrossBar = 6,

  HalfReverseCrow = 7,
  CurvedReverseCrow = 8,
  DoubleReverseCrow = 9,
  Crow = 10,
  DoubleFilledTriangle = 11,
  HollowTriangle = 12,
  DoubleHollowTriangle = 13,

  DoubleCrossBar = 14,
  SlantedBar = 15,
  TBar = 16,
  HollowDiamond = 17,

  FilledCircle = 18,
  HollowCircle = 19,
  FilledSquare = 20,
  HollowSquare = 21,

  CrowHollowCircle = 22,
  CrossBarHollowCircle = 23,
  CrowCrossBar = 24,
  TBarReverseCrow = 25,
  LargeHollowTriangle = 26,
  LargeFilledTriangle = 27,
  LargeHollowDiamond = 28,
  LargeFilledDiamond = 29,
}

export const enum AxShape {
  Indeterminate = -1,
  Rectangle = 0,
  // Rounded = 1,
  Top = 2,
  Bottom = 3,
  Left = 4,
  Right = 5,
  TabLeft = 6,
  TabRight = 7,
  TabBottomLeft = 8,
  TabBottomRight = 9,
  Placeholder = 10,
  TriangleUp = 11,
  TriangleRight = 12,
  TriangleDown = 13,
  TriangleLeft = 14,
  Ellipse = 15,
  ArrowButtonRight = 16,
  ArrowButtonLeft = 17,
  Drop = 18,
  SquareBracket = 19,
  CurlyBracket = 20,
  DropUpsideDown = 21,
  Star = 22,
  Heart = 23,
  Plus = 24,
  ArrowRight = 25,
  ArrowLeft = 26,
  SpeechBubbleRight = 27,
  SpeechBubbleLeft = 28, // not used in general but needed for conversion
  HorizontalLine = 29,
  VerticalLine = 30,
  ArrowDouble = 31,
  Pie = 32,
  RightTriangle = 33,
  StickyNote = 34,

  Paragraph = 100,
  H1 = 101,
  H2 = 102,
  H3 = 103,
  H4 = 104,
  H5 = 105,
  H6 = 106,

  // 200 numbering is used during conversion
  // Process = 200,
  // Rounded = 201,
  BeveledRectangle = 202,
  Diamond = 203,
  File = 204,
  Bracket = 205,
  Semicircle = 206,
  Triangle = 207,
  Trapezoid = 208,
  // Connector = 209,
  Hexagon = 210,
  Parallelogram = 211,
  Actor = 212,
  Database = 213,
  StackedRect = 214,
  // StackedRound = 215,
  StackedFile = 216,

  Vector = 300,
}

export const enum AxObjectType {
  None = 0,
  Shape = 1,
  Line = 2,
  ShapeLine = Shape | Line,
  Connector = 4,
  LineConnector = Line | Connector,
  ShapeLineConnector = ShapeLine | LineConnector,
  Image = 8,
  ShapeImage = Shape | Image,
  ShapeLineImage = ShapeLine | Image,
  ShapeLineConnectorImage = ShapeLineConnector | Image,
  Card = 16,
  ShapeLineCard = ShapeLine | Card,
  ShapeLineConnectorCard = ShapeLineConnector | Card,
  SmartLink = 32,
  Group = 64,
  Frame = 128,
  Section = 256,
  GroupFrameSection = Group | Frame | Section,
  PencilShape = 512,
  TextBox = 1024,
  TextArea = 2048,
  Droplist = 4096,
  ListBox = 8192,
  Checkbox = 16384,
  RadioButton = 32768,
  SelectionButtons = Checkbox | RadioButton,
  ShapeLineCardSelectionButtons = ShapeLineCard | SelectionButtons,
  FormFields = TextBox | TextArea | Droplist | ListBox | SelectionButtons,
  ShapeLineFormFields = ShapeLine | FormFields,
  ShapeLineConnectorFormFields = ShapeLineConnector | FormFields,
  ShapeImageFormFields = ShapeImage | FormFields,
  ShapeLineImageFormFields = ShapeLineImage | FormFields,
  ShapeLineConnectorImageFormFields = ShapeLineConnectorImage | FormFields,
  ShapeLineCardFormFields = ShapeLineCard | FormFields,
  ShapeLineConnectorCardFormFields = ShapeLineConnectorCard | FormFields,
  All = 65535,
}

export const enum AxScrollbars {
  Indeterminate = -1,
  None = 0,
  Both = 1,
  Vertical = 2,
  Horizontal = 3,
}

// TODO: Support combination of values?
export const enum AxConnectorType {
  Indeterminate = -1,
  RoundedTurn = 0,
  SharpTurn = 1,
  Line = 2,
  SimpleCurve = 3,
}

export interface AxShadow {
  On?: boolean;
  OffsetX?: number;
  OffsetY?: number;
  BlurRadius?: number;
  Spread?: number;
  Color?: ColorProp;
  ReferenceId?: string;
  ReferenceName?: string;
}

export interface AxBlur {
  On?: boolean;
  Radius?: number;
}

export interface AxArrowPair {
  First: AxArrowStyle;
  Second: AxArrowStyle;
}

export enum StrokeCap {
  Butt,
  Round,
  Square,
}

export enum StrokeAlignment {
  Center = 0,
  Inside = 1,
  Outside = 2,
}

export interface AxStrokeCapPair {
  StartCap: StrokeCap;
  EndCap: StrokeCap;
}

export interface AxSimpleRect {
  height: number;
  width: number;
  x: number;
  y: number;
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export interface AxConnectorInfo {
  hasConnectors: boolean;
  type: AxConnectorType;
}

export interface CornerRadius {
  TopLeft: number;
  TopRight: number;
  BottomRight: number;
  BottomLeft: number;
}

export interface StyleProp<T> {
  value: T;
  indeterminate: boolean;
  referenceId?: string;
  referenceName?: string;
}

export interface ColorProp {
  value: number;
  referenceId?: string | undefined;
  referenceName?: string | undefined;
}

export interface AxWidgetStyle {
  Family?: StyleProp<string>;
  Size?: StyleProp<number>;
  Weight?: StyleProp<number>;
  Style?: StyleProp<AxFontStyle>;
  Stretch?: StyleProp<FontStretch>;
  Underline?: StyleProp<boolean>;
  Color?: StyleProp<ColorProp>;
  HorizontalAlignment?: StyleProp<AxStringAlignment>;
  LineSpacing?: StyleProp<number>;
  CharacterSpacing?: StyleProp<number>;
  Strikethrough?: StyleProp<boolean>;
  ListInfo?: StyleProp<TextListInfo>;
  Baseline?: StyleProp<Baseline>;
  // BorderFill?: Fill,

  VerticalAlignment?: StyleProp<AxStringAlignment>;
  LetterCasing?: StyleProp<AxLetterCasing>;
  PaddingLeft?: StyleProp<number>;
  PaddingTop?: StyleProp<number>;
  PaddingRight?: StyleProp<number>;
  PaddingBottom?: StyleProp<number>;

  BorderFill?: StyleProp<FillList>;
  BorderVisibility?: StyleProp<AxPosition>;
  BorderWidth?: StyleProp<number>;
  StrokeAlignment?: StyleProp<StrokeAlignment>;
  // CornerRadius?: CornerRadiusProp;
  CornerRadiusTopLeft?: StyleProp<number>;
  CornerRadiusTopRight?: StyleProp<number>;
  CornerRadiusBottomRight?: StyleProp<number>;
  CornerRadiusBottomLeft?: StyleProp<number>;

  // Note: For now, use OuterShadow when "Shadow" prop is generically mentioned
  OuterShadow?: StyleProp<AxShadow>;
  InnerShadow?: StyleProp<AxShadow>;
  // TextShadow?: AxShadow,

  LinePattern?: StyleProp<AxLineStyle>;
  LineEnds?: StyleProp<AxArrowPair>;
  LineJoin?: StyleProp<AxLineJoin>;
  MiterAngle?: StyleProp<number>;
  // StrokeCaps?: AxStrokeCapPairProp;
  StrokeCapStart?: StyleProp<StrokeCap>;
  StrokeCapEnd?: StyleProp<StrokeCap>;
  // Note: Line Thickness shares itself with BorderWidthProp

  FixedWidth?: StyleProp<number>;
  FixedHeight?: StyleProp<number>;

  Location?: { X?: number; Y?: number };
  WidgetSize?: { Height?: number; Width?: number };
  Rotation?: StyleProp<number>;

  Fill?: StyleProp<FillList>;
  Opacity?: StyleProp<number>;

  BackImage?: StyleProp<string>;
  ImageAlignment?: StyleProp<number>;
  ImageProp?: StyleProp<number>;
  ImageRepeat?: StyleProp<number>;

  ButtonSize?: StyleProp<number>;

  WidgetBlur?: StyleProp<AxBlur>;
  BackdropBlur?: StyleProp<AxBlur>;
}

export interface AxFill {
  "fill-color": ColorProp;
  "fill-type": string;
}

export interface AxGradientFill extends AxFill {
  "fill-colors": readonly number[];
  "fill-offsets": readonly number[];
  "fill-ids": readonly string[];
  "fill-axis1": AxPoint;
  "fill-axis2": AxPoint;
  "fill-center-point": AxPoint;
}

export interface AxPoint {
  X: number;
  Y: number;
}

export interface FillItem {
  fill: AxFill;
  visibility: boolean;
}

export interface FillList {
  fills: FillItem[];
}

export interface AxFontFamilyVariant {
  label: string;
  weight: number;
  isItalic: boolean;
  url: string;
}

export const enum AnimationEasing {
  None = 0,

  // common
  Swing = 1,
  Linear = 2,

  // ease in
  EaseInSine = 8,
  EaseInQuad = 9,
  EaseInCubic = 3,
  EaseInQuart = 10,
  EaseInQuint = 11,
  EaseInExpo = 12,
  EaseInCirc = 13,
  EaseInBack = 14,
  EaseInElastic = 15,
  EaseInBounce = 16,

  // ease out
  EaseOutSine = 17,
  EaseOutQuad = 18,
  EaseOutCubic = 4,
  EaseOutQuart = 19,
  EaseOutQuint = 20,
  EaseOutExpo = 21,
  EaseOutCirc = 22,
  EaseOutBack = 23,
  EaseOutElastic = 7,
  EaseOutBounce = 6,

  // ease in out
  EaseInOutSine = 24,
  EaseInOutQuad = 25,
  EaseInOutCubic = 5,
  EaseInOutQuart = 26,
  EaseInOutQuint = 27,
  EaseInOutExpo = 28,
  EaseInOutCirc = 29,
  EaseInOutBack = 30,
  EaseInOutElastic = 31,
  EaseInOutBounce = 32,
}

export const enum EasingType {
  Invalid = -1,
  None = 0,
  Fade = 1,
  FadeAndScale = 2,
  Scale = 3,

  SlideRight = 4,
  SlideLeft = 5,
  SlideUp = 6,
  SlideDown = 7,

  FlipRight = 8,
  FlipLeft = 9,
  FlipUp = 10,
  FlipDown = 11,
}

export const enum DropType {
  Before = 0,
  After = 1,
  Inside = 2,
}

export interface AxLinkToObjectInfo {
  linkToObjectId: string;
  exitEasing: EasingType;
  // exitAnimation: AnimationEasing;
  // exitDuration: number;
  // enterEasing: EasingType;
  // enterAnimation: AnimationEasing;
  // enterDuration: number;
}

interface SelectionItem {
  // id: string;
  name: string;
  x: number;
  y: number;
  type: AxObjectType;
  autoFitHeight?: boolean;
  autoFitWidth?: boolean;
  fitToImage?: boolean;
  libraryLayerBehavior?: LibraryLayerBehavior;
}

export type ObjectSelectionItem = SelectionItem & {
  id: string;
};

export type InstanceSelectionItem = SelectionItem & {
  path: string;
};

export interface TrueObjectSelectionItem {
  id: string;
  name: string;
  displayTypeName: string;
  linkToObjectInfo?: AxLinkToObjectInfo;
}

export enum ExportFormat {
  png = 0,
  jpeg = 1,
  svg = 2,
  pdf = 3,
}

export enum ExportScale {
  x1 = 0,
  x0_5 = 1,
  x0_75 = 2,
  x1_5 = 3,
  x2 = 4,
  x3 = 5,
  x4 = 6,
  w512 = 7,
  h512 = 8,
}

export interface ExportInfo {
  format: ExportFormat;
  scale: ExportScale;
  suffix: string;
  ignoreOverlappingElements: boolean;
}

export type ExportList = ExportInfo[];

export interface Selection {
  selectedItems: TrueObjectSelectionItem[];
  items: ObjectSelectionItem[];
  // instanceItems: InstanceSelectionItem[];
  rect?: AxSimpleRect;
  connectors?: AxConnectorInfo;
  shape: AxShape;
  style?: AxWidgetStyle;
  fillAndBorderStyle?: AxWidgetStyle;
  type: AxObjectType;
  hasNonVectorImage: boolean;
  supportsText: boolean;
  hasText: boolean;
  exports?: ExportList;
  hasRectangle: boolean;
}

const selectionChangedEvent = Symbol("bridge:selectionChanged");
export const {
  emitter: selectionChanged,
  listener: onSelectionChanged,
} = useBusEmitterListener<{ objectIds: string[] }>(selectionChangedEvent);

export enum ShapeManagerItemType {
  Vertex = "vertex",
  Handle = "handle",
}

export interface ShapeManagerPoint {
  x: number;
  y: number;
  cornerRadius?: number;
  type: ShapeManagerItemType;
}

export const getSelection = useBinaryReader(getCurrentSelection);
export const getThumbnail = (objectId: string) => getSelectionImgThumbnail(objectId);
export const getContextMenuItem = getSelectionContextMenuItem;
export const getLibraryBehaviorContextMenuItem = getLibraryBehaviorContextMenuItemProxy;
export const showCurrentContextMenu = (objectId: string, x: number, y: number) => showSelectionContextMenu(objectId, x, y);
export const getShapeManagerSelection = useBinaryReader(getShapeManagerSelectedPoints);
export const getGradientModeGeometry = useBinaryReader(getGradientModeGeometryInfo);
